import type { ReactNode } from "react"
import { createContext, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useHistory } from "react-router-dom"
import { toast } from "sonner"

import {
  addLesson,
  createCourse,
  createCourseVersion,
  createLesson,
  createLessonVersion,
  getCourse,
  getLesson,
  publishCourse,
  publishLesson,
  saveVaasCourse,
  unpublishCourse,
  unpublishLesson,
  updateCourse,
  updateLesson,
} from "~/v2/api/vaas"
import { VAAS_COURSES_QUERY_KEY } from "~/v2/lib/vaas-query-keys"
import { buildVaasLessonName } from "~/v2/lib/vaas-schema"
import type {
  VaasCourse,
  VaasCreateValues,
  VaasEditValues,
  VaasId,
  VaasLesson,
  VaasLessonSummary,
  VaasThumbnail,
} from "~/v2/types/vaas"
import { getErrorMessage, openV2ErrorToast } from "~/v2/utils/toast"
import { validateVaasForPublish } from "~/v2/utils/vaas-validation"

const VaasContext = createContext<any>(null)

type ApiData = Record<string, any>
type DirtyFields = Record<string, any>
type VaasValues = VaasCreateValues | VaasEditValues | ApiData

const resolveLessonId = (lesson: VaasLessonSummary) =>
  lesson?.id || lesson?.lesson_id || null
const isPublishedRecord = (record?: VaasCourse | VaasLesson | null) =>
  record?.published === 1
const isUnpublishedRecord = (record?: VaasCourse | VaasLesson | null) =>
  record?.published === 0
const isAiCreatedBy = (createdBy?: string | null) =>
  createdBy === "modified-ai" || createdBy === "created-ai"
const serializeThumbnailForApi = (thumbnail?: VaasThumbnail | null) => {
  if (!thumbnail || !thumbnail.public_id) {
    return null
  }

  return JSON.stringify({
    format: thumbnail.format,
    originial_filename:
      thumbnail.originial_filename || thumbnail.original_filename || null,
    public_id: thumbnail.public_id,
    secure_url: thumbnail.secure_url,
    url: thumbnail.url,
    version: thumbnail.version,
  })
}

const buildLessonMetadataPayload = (values: VaasValues): ApiData => {
  const isAi = isAiCreatedBy(values.created_by)

  return {
    created_by: values.created_by || null,
    vaas_creation_timestamp: isAi
      ? values.vaas_creation_timestamp || null
      : null,
    vaas_provider_name: isAi ? values.vaas_provider_name || null : null,
    vaas_system_version: isAi ? values.vaas_system_version || null : null,
  }
}

export default VaasContext

export const VaasProvider = ({
  children,
  courseId = null,
}: {
  children: ReactNode
  courseId?: VaasId | null
}) => {
  const [selectedStatus, setSelectedStatus] = useState("all")
  const [createError, setCreateError] = useState("")
  const [saveError, setSaveError] = useState("")
  const [actionError, setActionError] = useState("")
  const queryClient = useQueryClient()
  const history = useHistory()

  const createCourseMutation = useMutation((data: ApiData) =>
    createCourse(data),
  )
  const updateCourseMutation = useMutation(
    ({ courseId, data }: { courseId: VaasId; data: ApiData }) =>
      updateCourse(data, courseId),
  )
  const createLessonMutation = useMutation((data: ApiData) =>
    createLesson(data),
  )
  const attachLessonMutation = useMutation(
    ({ courseId, data }: { courseId: VaasId; data: ApiData[] }) =>
      addLesson(data, courseId),
  )
  const updateLessonMutation = useMutation(
    ({ lessonId, data }: { lessonId: VaasId; data: ApiData }) =>
      updateLesson(data, lessonId),
  )
  const publishCourseMutation = useMutation((id: VaasId) => publishCourse(id))
  const unpublishCourseMutation = useMutation((id: VaasId) =>
    unpublishCourse(id),
  )
  const saveVaasCourseMutation = useMutation((id: VaasId) => saveVaasCourse(id))
  const publishLessonMutation = useMutation((id: VaasId) => publishLesson(id))
  const unpublishLessonMutation = useMutation((id: VaasId) =>
    unpublishLesson(id),
  )
  const newCourseVersionMutation = useMutation((data: ApiData) =>
    createCourseVersion(data, courseQuery.data?.id),
  )
  const newLessonVersionMutation = useMutation((data: ApiData) =>
    createLessonVersion(data, lessonQuery.data?.id),
  )

  const courseQuery = useQuery(
    ["vaas-course", courseId],
    () => getCourse(courseId),
    {
      enabled: Boolean(courseId),
      cacheTime: 0,
    },
  )

  const attachedLessons = courseQuery.data?.lessons || []
  const lessonId =
    attachedLessons.length === 1 ? resolveLessonId(attachedLessons[0]) : null

  const lessonQuery = useQuery(
    ["vaas-lesson", lessonId],
    () => getLesson(lessonId),
    {
      enabled:
        Boolean(courseId) && attachedLessons.length === 1 && Boolean(lessonId),
      cacheTime: 0,
    },
  )

  const createVaas = async (values: VaasCreateValues) => {
    setCreateError("")

    let createdCourse = null
    let createdLesson = null

    try {
      createdCourse = await createCourseMutation.mutateAsync({
        name: values.course_name,
      })
    } catch (err) {
      setCreateError("Course creation failed. No VAAS item was created.")
      openV2ErrorToast(err, "Error creating VAAS course")
      throw err
    }

    try {
      createdCourse = await updateCourseMutation.mutateAsync({
        courseId: createdCourse!.id,
        data: {
          course_type: values.course_type,
          disable_final_assessment: values.disable_final_assessment,
          name: values.course_name,
          thumbnail: serializeThumbnailForApi(values.course_thumbnail),
          vaas_logo_redirect: values.vaas_logo_redirect,
          vaas_show_feedback: values.vaas_show_feedback,
        },
      })
    } catch (err) {
      const message = `Course created, but VAAS course setup failed. Course ID: ${createdCourse!.id}.`
      setCreateError(message)
      openV2ErrorToast(err, message)
      throw err
    }

    try {
      createdLesson = await createLessonMutation.mutateAsync({
        ...buildLessonMetadataPayload(values),
        description: values.lesson_description,
        name: buildVaasLessonName(values.course_name),
      })
    } catch (err) {
      const message = `Course created, but lesson creation failed. Course ID: ${createdCourse!.id}.`
      setCreateError(message)
      openV2ErrorToast(err, message)
      throw err
    }

    try {
      await attachLessonMutation.mutateAsync({
        courseId: createdCourse!.id,
        data: [{ lesson_id: createdLesson!.id }],
      })
    } catch (err) {
      const message = `Course and lesson created, but attach failed. Course ID: ${createdCourse!.id}. Lesson ID: ${createdLesson!.id}.`
      setCreateError(message)
      openV2ErrorToast(err, message)
      history.push(`/vaas/${createdCourse!.id}`)
      throw err
    }

    const nextCourse = await getCourse(createdCourse!.id)
    const nextLesson = await getLesson(createdLesson!.id)

    if (!nextCourse || !nextLesson) {
      const message =
        "VAAS was created, but its course or lesson could not be reloaded. Refresh to review its current state."
      setCreateError(message)
      toast.error(message)
      throw new Error(message)
    }

    queryClient.setQueryData(["vaas-course", nextCourse.id], nextCourse)
    queryClient.setQueryData(["vaas-lesson", nextLesson.id], nextLesson)
    queryClient.setQueryData<VaasCourse[] | undefined>(
      VAAS_COURSES_QUERY_KEY,
      (oldData) =>
        Array.isArray(oldData) ? [...oldData, nextCourse] : oldData,
    )
    queryClient.setQueryData("lessons", (oldData: any[] = []) => [
      ...oldData,
      nextLesson,
    ])
    queryClient.invalidateQueries(VAAS_COURSES_QUERY_KEY)
    queryClient.invalidateQueries("lessons")

    toast.success("VAAS created")
    history.push(`/vaas/${nextCourse.id}`)

    return {
      course: nextCourse,
      lesson: nextLesson,
    }
  }

  const syncCourseCache = async (course: VaasCourse) => {
    queryClient.setQueryData(["vaas-course", course.id], course)
    queryClient.setQueryData(["course", course.id], course)
    queryClient.setQueryData<VaasCourse[] | undefined>(
      VAAS_COURSES_QUERY_KEY,
      (oldData) => {
        if (!Array.isArray(oldData)) {
          return oldData
        }

        const hasMatch = oldData.some((item) => item.id === course.id)
        if (!hasMatch) {
          return [...oldData, course]
        }

        return oldData.map((item) => (item.id === course.id ? course : item))
      },
    )

    return course
  }

  const syncLessonCache = async (lesson: VaasLesson) => {
    queryClient.setQueryData(["vaas-lesson", lesson.id], lesson)
    queryClient.setQueryData(["lesson", lesson.id], lesson)
    queryClient.setQueryData("lessons", (oldData: VaasLesson[] = []) => {
      const hasMatch = oldData.some((item) => item.id === lesson.id)
      if (!hasMatch) {
        return [...oldData, lesson]
      }

      return oldData.map((item) => (item.id === lesson.id ? lesson : item))
    })

    return lesson
  }

  const refetchDetail = async () => {
    const nextCourseResult = await courseQuery.refetch()
    const nextCourse = nextCourseResult.data || courseQuery.data || null

    let nextLesson = null
    if (lessonId || lessonQuery.data?.id) {
      const nextLessonResult = await lessonQuery.refetch()
      nextLesson = nextLessonResult.data || lessonQuery.data || null
    }

    return {
      course: nextCourse,
      lesson: nextLesson,
    }
  }

  const saveVaas = async (values: VaasEditValues, dirtyFields: DirtyFields) => {
    setSaveError("")
    setActionError("")

    const nextDirtyFields = dirtyFields || {}
    const coursePayload: ApiData = {}
    const lessonPayload: ApiData = {}

    if (
      !courseQuery.data?.disable_final_assessment ||
      nextDirtyFields.disable_final_assessment
    ) {
      coursePayload.disable_final_assessment = true
    }

    if (nextDirtyFields.course_name) {
      coursePayload.name = values.course_name
      lessonPayload.name = buildVaasLessonName(values.course_name)
    }
    if (nextDirtyFields.course_thumbnail) {
      coursePayload.thumbnail = serializeThumbnailForApi(
        values.course_thumbnail,
      )
    }
    if (nextDirtyFields.vaas_logo_redirect) {
      coursePayload.vaas_logo_redirect = values.vaas_logo_redirect
    }
    if (nextDirtyFields.vaas_show_feedback) {
      coursePayload.vaas_show_feedback = values.vaas_show_feedback
    }
    if (nextDirtyFields.lesson_description) {
      lessonPayload.description = values.lesson_description
    }
    if (
      nextDirtyFields.created_by ||
      nextDirtyFields.vaas_provider_name ||
      nextDirtyFields.vaas_system_version ||
      nextDirtyFields.vaas_creation_timestamp
    ) {
      Object.assign(lessonPayload, buildLessonMetadataPayload(values))
    }
    if (Object.keys(lessonPayload).length > 0) {
      lessonPayload.video = {}
    }

    try {
      if (Object.keys(lessonPayload).length > 0) {
        const lessonResponse = await updateLessonMutation.mutateAsync({
          data: lessonPayload,
          lessonId: lessonQuery.data!.id,
        })
        await syncLessonCache(lessonResponse as VaasLesson)
      }
    } catch (err) {
      const message = "Lesson save failed. Course changes were not saved."
      setSaveError(message)
      openV2ErrorToast(err, message)
      throw err
    }

    try {
      if (Object.keys(coursePayload).length > 0) {
        const courseResponse = await updateCourseMutation.mutateAsync({
          courseId: courseQuery.data!.id,
          data: coursePayload,
        })
        await syncCourseCache(courseResponse as VaasCourse)
      }
    } catch (err) {
      const message =
        "Course save failed after lesson save. Refresh to review current state."
      setSaveError(message)
      openV2ErrorToast(err, message)
      throw err
    }

    const nextState = await refetchDetail()

    toast.success("VAAS updated")

    return nextState
  }

  const saveVaasCourseToKv = async () => {
    setActionError("")

    try {
      const response = await saveVaasCourseMutation.mutateAsync(
        courseQuery.data!.id,
      )
      toast.success(response.message || "VaaS course saved")
      return response
    } catch (err) {
      const message = getErrorMessage(err, "Error saving VaaS course")
      setActionError(message)
      openV2ErrorToast(err, message)
      return null
    }
  }

  const publishVaas = async ({
    dirtyFields,
    values,
  }: {
    dirtyFields: DirtyFields
    values: VaasEditValues
  }) => {
    setActionError("")
    setSaveError("")

    const validationError = validateVaasForPublish({
      course: courseQuery.data,
      lesson: lessonQuery.data,
      values,
    })

    if (validationError) {
      setActionError(validationError)
      toast.error(validationError)
      throw new Error(validationError)
    }

    let nextState = {
      course: courseQuery.data,
      lesson: lessonQuery.data,
    }

    if (!nextState.course?.disable_final_assessment) {
      const courseResponse = await updateCourseMutation.mutateAsync({
        courseId: nextState.course!.id,
        data: { disable_final_assessment: true },
      })
      nextState.course = await syncCourseCache(courseResponse as VaasCourse)
    }

    if (Object.keys(dirtyFields || {}).length > 0) {
      nextState = await saveVaas(values, dirtyFields)
    }

    if (!isPublishedRecord(nextState.lesson)) {
      try {
        const lessonResponse = await publishLessonMutation.mutateAsync(
          nextState.lesson!.id,
        )
        nextState.lesson = await syncLessonCache(lessonResponse as VaasLesson)
      } catch (err) {
        const message = "Lesson publish failed. Course was not published."
        setActionError(message)
        openV2ErrorToast(err, message)
        throw err
      }
    }

    if (!isPublishedRecord(nextState.course)) {
      try {
        const courseResponse = await publishCourseMutation.mutateAsync(
          nextState.course!.id,
        )
        nextState.course = await syncCourseCache(courseResponse as VaasCourse)
      } catch (err) {
        const message =
          "Course publish failed after lesson publish. Refresh to review current state."
        setActionError(message)
        await refetchDetail()
        openV2ErrorToast(err, message)
        throw err
      }
    }

    nextState = await refetchDetail()
    toast.success("VAAS published")

    return nextState
  }

  const unpublishVaas = async ({
    dirtyFields,
    values,
  }: {
    dirtyFields: DirtyFields
    values: VaasEditValues
  }) => {
    setActionError("")
    setSaveError("")

    let nextState = {
      course: courseQuery.data,
      lesson: lessonQuery.data,
    }

    if (Object.keys(dirtyFields || {}).length > 0) {
      nextState = await saveVaas(values, dirtyFields)
    }

    if (!isUnpublishedRecord(nextState.course)) {
      try {
        const courseResponse = await unpublishCourseMutation.mutateAsync(
          nextState.course!.id,
        )
        nextState.course = await syncCourseCache(courseResponse as VaasCourse)
      } catch (err) {
        const message = "Course unpublish failed. Lesson state was not changed."
        setActionError(message)
        openV2ErrorToast(err, message)
        throw err
      }
    }

    if (!isUnpublishedRecord(nextState.lesson)) {
      try {
        const lessonResponse = await unpublishLessonMutation.mutateAsync(
          nextState.lesson!.id,
        )
        nextState.lesson = await syncLessonCache(lessonResponse as VaasLesson)
      } catch (err) {
        const message =
          "Lesson unpublish failed after course unpublish. Refresh to review current state."
        setActionError(message)
        await refetchDetail()
        openV2ErrorToast(err, message)
        throw err
      }
    }

    nextState = await refetchDetail()
    toast.success("VAAS unpublished")

    return nextState
  }

  const createCombinedVersion = async () => {
    setActionError("")
    setSaveError("")

    let nextLessonVersion = null
    let nextCourseVersion = null

    try {
      const lessonVersion =
        lessonQuery.data?.versions?.[lessonQuery.data.versions.length - 1]
      const lessonSplit = lessonVersion?.version?.split(".") || ["1", "0", "0"]
      const nextLessonVersionString =
        Number(lessonSplit[2]) < 9
          ? `${lessonSplit[0]}.${lessonSplit[1]}.${Number(lessonSplit[2]) + 1}`
          : Number(lessonSplit[1]) < 9
            ? `${lessonSplit[0]}.${Number(lessonSplit[1]) + 1}.0`
            : `${Number(lessonSplit[0]) + 1}.0.0`

      const lessonResponse = await newLessonVersionMutation.mutateAsync({
        version: nextLessonVersionString,
      })
      nextLessonVersion = lessonResponse
    } catch (err) {
      const message =
        "Lesson version creation failed. Course version was not created."
      setActionError(message)
      openV2ErrorToast(err, message)
      throw err
    }

    try {
      const courseVersion =
        courseQuery.data?.versions?.[courseQuery.data.versions.length - 1]
      const courseSplit = courseVersion?.version?.split(".") || ["1", "0", "0"]
      const nextCourseVersionString =
        Number(courseSplit[2]) < 9
          ? `${courseSplit[0]}.${courseSplit[1]}.${Number(courseSplit[2]) + 1}`
          : Number(courseSplit[1]) < 9
            ? `${courseSplit[0]}.${Number(courseSplit[1]) + 1}.0`
            : `${Number(courseSplit[0]) + 1}.0.0`

      const courseResponse = await newCourseVersionMutation.mutateAsync({
        version: nextCourseVersionString,
      })
      nextCourseVersion = courseResponse
    } catch (err) {
      const message =
        "Lesson version was created, but course version failed. Refresh to review current state."
      setActionError(message)
      openV2ErrorToast(err, message)
      throw err
    }

    queryClient.invalidateQueries(VAAS_COURSES_QUERY_KEY)
    queryClient.invalidateQueries("lessons")
    toast.success("VAAS version created")
    history.push(`/vaas/${nextCourseVersion!.id}`)

    return {
      course: nextCourseVersion,
      lesson: nextLessonVersion,
    }
  }

  const value = {
    actionError,
    createCombinedVersion,
    course: courseQuery.data || null,
    courseError: courseQuery.error || null,
    courseId,
    detailLoading: courseQuery.isLoading || lessonQuery.isLoading,
    createError,
    createLoading:
      createCourseMutation.isLoading ||
      updateCourseMutation.isLoading ||
      createLessonMutation.isLoading ||
      attachLessonMutation.isLoading,
    createVaas,
    guardStatus: !courseId
      ? "idle"
      : courseQuery.isLoading
        ? "loading"
        : courseQuery.isError
          ? "course_error"
          : attachedLessons.length === 0
            ? "no_lesson"
            : attachedLessons.length > 1
              ? "multiple_lessons"
              : lessonQuery.isLoading
                ? "loading"
                : lessonQuery.isError
                  ? "lesson_error"
                  : "ready",
    lesson: lessonQuery.data || null,
    lessonError: lessonQuery.error || null,
    lessonId,
    publishLoading:
      publishCourseMutation.isLoading || publishLessonMutation.isLoading,
    publishVaas,
    saveError,
    saveLoading:
      updateCourseMutation.isLoading || updateLessonMutation.isLoading,
    saveVaas,
    saveVaasCourseToKv,
    refetchVaas: async () => {
      await courseQuery.refetch()
      if (lessonId) {
        await lessonQuery.refetch()
      }
    },
    selectedStatus,
    setSelectedStatus,
    unpublishLoading:
      unpublishCourseMutation.isLoading || unpublishLessonMutation.isLoading,
    unpublishVaas,
    vaasCourseSaveLoading: saveVaasCourseMutation.isLoading,
    versionLoading:
      newCourseVersionMutation.isLoading || newLessonVersionMutation.isLoading,
  }

  return <VaasContext.Provider value={value}>{children}</VaasContext.Provider>
}
